'use client';

import React, { useState } from 'react';

export default function JoinSection() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/send-welcome-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Welcome email error:', data.error);
        // Still show success to user — form submission worked even if email fails
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section suppressHydrationWarning id="join" className="py-28 bg-navy-dark relative overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80')` }}
        />
        <div className="absolute inset-0 bg-luxury-gradient opacity-90" />
      </div>

      {/* Decorative circles */}
      <div className="absolute top-1/2 left-0 w-96 h-96 rounded-full bg-gold/5 blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute top-1/2 right-0 w-96 h-96 rounded-full bg-gold/5 blur-3xl translate-x-1/2 -translate-y-1/2" />

      <div suppressHydrationWarning className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — Copy */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-gold" />
              <span className="text-gold text-xs tracking-[0.3em] uppercase font-light">Membership</span>
            </div>
            <h2 className="font-serif text-white text-4xl md:text-5xl font-light leading-tight mb-6">
Join the Ultra Luxury Cruise Club and receive an exclusive members only discount off every cruise.
            </h2>
            <p className="text-white/50 font-light leading-relaxed mb-10 text-lg">
              Membership is completely free for a limited time. Once you join, every ultra luxury cruise booking earns you an exclusive members only discount.
            </p>

            {/* Benefits list */}
            <div className="space-y-4">
              {[
                'Exclusive member pricing on every cruise',
                'Access to exclusive member-only rates',
                'Dedicated concierge booking support',
                'First access to new cruise releases',
              ].map((benefit) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-white/60 font-light text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-serif text-white text-2xl font-light mb-3">Welcome Aboard</h3>
                <p className="text-white/50 font-light leading-relaxed">
                  Thank you for joining the Ultra Luxury Cruise Club. We&apos;ll be in touch shortly with your membership details.
                </p>
                <a
                  href="https://luxury-travel-club.cruiseco.com.au/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-gold-gradient text-navy-dark text-sm font-medium tracking-[0.1em] uppercase rounded-sm"
                >
                  Explore Cruises Now
                </a>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-white text-2xl font-light mb-2">Become a Member</h3>
                <p className="text-white/40 text-sm font-light mb-8">Free to join. Instant access to member pricing.</p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div>
                      <label className="block text-white/50 text-xs tracking-[0.15em] uppercase mb-2 font-light">
                        Full Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-white/20 text-sm font-light focus:outline-none focus:border-gold/50 transition-colors duration-200"
                        placeholder="Your full name"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-white/50 text-xs tracking-[0.15em] uppercase mb-2 font-light">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-white/20 text-sm font-light focus:outline-none focus:border-gold/50 transition-colors duration-200"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-white/50 text-xs tracking-[0.15em] uppercase mb-2 font-light">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-sm px-4 py-3 text-white placeholder-white/20 text-sm font-light focus:outline-none focus:border-gold/50 transition-colors duration-200"
                      placeholder="your@email.com"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-gold-gradient text-navy-dark font-medium text-sm tracking-[0.15em] uppercase rounded-sm hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Joining...
                      </>
                    ) : (
                      'Join The Club — It\'s Free'
                    )}
                  </button>

                  {error && (
                    <p className="text-center text-red-400 text-xs font-light mt-2">{error}</p>
                  )}

                  <p className="text-center text-white/30 text-xs font-light mt-4 tracking-wide">No fees. No commitments.</p>

                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
